import { useEffect, useState, createRef } from "react";
import { Button, CircularProgress, Radio, RadioGroup, FormControlLabel } from "@material-ui/core";
import { FormikDatePicker, FormikTextFieldPx, Modal, useToast, useMutation, useQuery } from "@webapps/reactjs-tech-components";
import { useStyles } from "@webapps/styles";
import { makeStyles } from "@material-ui/core/styles";
import { isSameDay } from "date-fns";
import { getBusinessEmailOfAStudentInAClassroom, postAbsenceBusinessEmail, postStudentAbsence, getClassroomInfoById, getInfosAccountRestricted } from "@webapps/business";
import { useFormik } from "formik";
import StudentStatementForMailing from "pages/school/ClassesPage/ClassesDetail/Components/StudentStatementForMailing";
import InfoError from "components/InfoError";

const BlueRadio = {
  root: {
    color: "#0060F9",
    '&$checked': {
      color: "#0060F9",
    },
  },
  checked: {},
};

const useLocalStyles = makeStyles(({ spacing, palette }) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing(2),
    backgroundColor: "red",
  },
  absenceForm: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    width: '100%',
    gap: "10px"
  },
  infoBox: {
    borderRadius: '5px',
    color: '#152652',
    "-webkit-text-fill-color": "#152652",
    '@media only screen and (max-width:1920px)': { fontSize: '13.500px' },
    '@media only screen and (max-width:1600px)': { fontSize: '13.5000px' },
    fontFamily: 'Montserrat',
    height: '30px',
    backgroundColor: '#F3F7FF',
    display: 'flex',
    alignItems: 'center',
  },
  radioContainer: {
    // marginBottom: '20px',
    marginTop: '8px',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start'
  },
  hourInput: {
    // marginBottom: "16px"
  },
  confirmButton: {
    marginTop: "30px",
  },
  duoTime: {
    display: 'flex',
    justifyContent: 'space-between',
  }
}));

const InputDuoStartDate = ({ formik, hoursPerDay, handleChangeHour, handleChangeMinute, classes }) => {
  return (
    <>
      <div className={classes.hourInput}>
        <FormikTextFieldPx
          formik={formik}
          fullWidth
          type="number"
          borderRadius={6}
          label="Heures d'absence"
          onChange={(e) => handleChangeHour(e, 0, hoursPerDay)}
          name="hoursFirstDay"
          inputProps={{
            step: 1,
            min: 0,
            max: hoursPerDay,
          }}
          variant="outlined"
        />
      </div>

      <div className={classes.hourInput}>
        <FormikTextFieldPx
          formik={formik}
          fullWidth
          type="number"
          borderRadius={6}
          label="Minutes d'absence"
          onChange={(e) => handleChangeMinute(e)}
          name="minutesFirstDay"
          inputProps={{
            step: 1,
            min: 0,
            max: 59,
          }}
          variant="outlined"
        />
      </div>
    </>
  )
}

const AbsenceDeclarationModal = ({ open, onClose, student, classroomId, onConfirm }) => {
  const classes = useLocalStyles();
  const { global, cx, css } = useStyles();
  const { addToast } = useToast();

  const [emailSendedLoading, setEmailSendedLoading] = useState(false);
  const [displayStatementForMailing, setDisplayStatementForMailing] = useState(false);
  const [absenceToExport, setAbsenceToExport] = useState([]);
  const [businessEmail, setBusinessEmail] = useState('');
  const [refStatement, setRefStatement] = useState(createRef());
  const [downloadStatement, setDownloadStatement] = useState(false);

  const {
    isLoading: isAddAbsenceLoading,
    isSuccess: isAddAbsenceSuccess,
    error: isAddAbsenceError,
    mutateAsync: mutatePostStudentAbsence,
  } = useMutation(postStudentAbsence);

  const { mutateAsync: mutatePostAbsenceBusinessEmail } = useMutation(postAbsenceBusinessEmail);

  const { mutateAsync: mutateGetBusinessEmailOfAStudentInAClassroom } = useMutation(getBusinessEmailOfAStudentInAClassroom);

  const { data: classroom = {} } = useQuery(getClassroomInfoById, {
    key: ['classroom', classroomId],
    variables: { id: classroomId },
  })

  const { data: infoAccount = {} } = useQuery(getInfosAccountRestricted, {
    key: ['infoAccount'],
    variables: { id: student?.team?.business?.id },
    enabled: !!student?.team?.business?.id,
  })

  const formik = useFormik({
    initialValues: {
      startDate: new Date(),
      startTime: "08:00",
      endDate: new Date(),
      endTime: "18:00",
      reason: "",
      isAbsenceAllStartDate: "false",
      isAbsenceAllEndDate: "false",
      hoursFirstDay: null,
      minutesFirstDay: null,
      hoursLastDay: null,
      minutesLastDay: null,
      business_email: "",
    },
    _onSubmit: async (values) => {
      setEmailSendedLoading(true);

      const isEmpty = (val) => val === undefined || val === null || val === "";
      const isSameDay =
        values.startDate &&
        values.endDate &&
        new Date(values.startDate).toISOString().slice(0, 10) ===
        new Date(values.endDate).toISOString().slice(0, 10);

      const validateMinutes = (minute) => {
        const num = Number(minute);
        return Number.isInteger(num) && num >= 0 && num <= 59;
      };

      // PREMIER JOUR
      if (
        values.isAbsenceAllStartDate === "false" &&
        isEmpty(values.hoursFirstDay) &&
        isEmpty(values.minutesFirstDay)
      ) {
        addToast({
          status: "warning",
          message:
            "Veuillez saisir au moins les heures ou les minutes d'absence pour le premier jour.",
        });
        setEmailSendedLoading(false);
        return;
      }
      if (
        !isEmpty(values.minutesFirstDay) &&
        !validateMinutes(values.minutesFirstDay)
      ) {
        addToast({
          status: "warning",
          message:
            "Veuillez saisir un nombre de minutes valide (max 59) pour le premier jour.",
        });
        setEmailSendedLoading(false);
        return;
      }

      // DERNIER JOUR (on ne valide que si ce n'est pas le même jour)
      if (
        !isSameDay &&
        values.isAbsenceAllEndDate === "false" &&
        isEmpty(values.hoursLastDay) &&
        isEmpty(values.minutesLastDay)
      ) {
        addToast({
          status: "warning",
          message:
            "Veuillez saisir au moins les heures ou les minutes d'absence pour le dernier jour.",
        });
        setEmailSendedLoading(false);
        return;
      }
      if (
        !isSameDay &&
        !isEmpty(values.minutesLastDay) &&
        !validateMinutes(values.minutesLastDay)
      ) {
        addToast({
          status: "warning",
          message:
            "Veuillez saisir un nombre de minutes valide (max 59) pour le dernier jour.",
        });
        setEmailSendedLoading(false);
        return;
      }

      // RAISON
      if (!values?.reason) {
        addToast({
          status: "warning",
          message: "Veuillez sélectionner une raison avant de soumettre.",
        });
        setEmailSendedLoading(false);
        return;
      }

      // EMAIL
      if (
        values?.business_email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values?.business_email)
      ) {
        addToast({
          status: "warning",
          message: "Veuillez saisir un email valide",
        });
        setEmailSendedLoading(false);
        return;
      }

      try {
        setBusinessEmail(values?.business_email);
        const { data } = await mutatePostStudentAbsence({
          studentId: student?.id,
          classId: classroomId,
          data: values,
        });

        if (values?.business_email) {
          await mutatePostAbsenceBusinessEmail({
            studentId: student?.id,
            classId: classroomId,
            data: { email: values?.business_email },
          });
        }

        setAbsenceToExport(data);
        setDisplayStatementForMailing(true);
      } catch (error) {
        console.error("Erreur lors de la déclaration de l'absence :", error);
        setEmailSendedLoading(false);
        setBusinessEmail("");
      }
    },
    get onSubmit() {
      return this._onSubmit;
    },
    set onSubmit(value) {
      this._onSubmit = value;
    },
  });

  useEffect(() => {
    const getBusinessEmail = async () => {
      const { data } = await mutateGetBusinessEmailOfAStudentInAClassroom({
        studentId: student?.id,
        classId: classroomId,
      });

      formik.setFieldValue("business_email", data.email);
    };

    if (student?.id && classroomId) {
      getBusinessEmail();
    }
  }, [student?.id, classroomId, open]);
  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  useEffect(() => {
    if (isAddAbsenceSuccess) {

      // handleClose();
      if (onConfirm) {
        onConfirm();
      }
    }

    if (isAddAbsenceError) {
      addToast({
        status: "error",
        message: isAddAbsenceError?.response?.data?.description,
      });
    }
  }, [isAddAbsenceSuccess, isAddAbsenceError]);

  const handleChangeHour = (e, min, max) => {
    const { value, name } = e.target;
    if (value >= min && value <= max) {
      formik.setFieldValue(name, value);
    }
  };

  const handleChangeMinute = (e) => {
    const { value, name } = e.target;
    if (value >= 0 && value < 60) {
      formik.setFieldValue(name, value);
    }
  };


  const handleOnDownloadComplete = () => {
    addToast({
      status: "success",
      message: "Absence déclarée avec succès. Mail envoyé à l'étudiant et à l'entreprise",
    });
    setDownloadStatement(false);
    setEmailSendedLoading(false);
    setDisplayStatementForMailing(false);
    formik.resetForm();
    onClose();
  };

  const getPeriodHours = (startDate, endDate) => {
    const allPeriods = classroom?.planning?.periods || [];
    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);

    const startDateString = startDateTime.toISOString().slice(0, 10);
    const endDateString = endDateTime.toISOString().slice(0, 10);

    // Find a period that overlaps with the given date range
    const period = allPeriods.find((period) => {
      const periodStart = new Date(period.startDate);
      const periodEnd = new Date(period.endDate);
      const inputStart = new Date(startDateString);
      const inputEnd = new Date(endDateString);
      return inputStart <= periodEnd && inputEnd >= periodStart;
    });

    // Return hoursPerDay or 7 if null
    return period ? (period.hoursPerDay !== null ? period.hoursPerDay : 7) : 7;
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      className={classes.modal}
      title="Déclarer une absence"
      maxWdth={500}
    >
      {displayStatementForMailing && (
        <StudentStatementForMailing
          ref={refStatement}
          student={student}
          classroom={classroom}
          downloadStatement={downloadStatement}
          onDownloadComplete={() => handleOnDownloadComplete()}
          absenceToExport={absenceToExport?.absences}
          setDownloadStatement={setDownloadStatement}
          setEmailSendedLoading={setEmailSendedLoading}
          businessEmail={infoAccount?.email || businessEmail}
        />
      )
      }
      <div className={classes.modalContent}>
        <div className={classes.absenceForm}>
          <div className={css({
            // marginBottom: 16
          })}>
            <FormikDatePicker
              formik={formik}
              fullWidth
              borderRadius={6}
              label="Date de début"
              name="startDate"
              variant="outlined"
            />
          </div>

          {!(isSameDay(formik?.values?.startDate, formik?.values?.endDate)) && formik?.values?.isAbsenceAllStartDate === "false" && (
            <InputDuoStartDate
              formik={formik}
              hoursPerDay={getPeriodHours(formik?.values?.startDate, formik?.values?.endDate)}
              handleChangeHour={handleChangeHour}
              handleChangeMinute={handleChangeMinute}
              classes={classes}
            />
          )}


          {!(isSameDay(formik?.values?.startDate, formik?.values?.endDate)) && (
            <div className={classes.radioContainer}>
              <div>Absent(e) toute la journée ?</div>
              <RadioGroup
                aria-labelledby="absenceAllStartDate"
                value={formik?.values?.isAbsenceAllStartDate}
                name="absenceAllStartDate"
                onChange={(e) => {
                  formik.setFieldValue("isAbsenceAllStartDate", e.target.value);
                  if (e.target.value === "true") {
                    formik.setFieldValue("hoursFirstDay", getPeriodHours(formik?.values?.startDate, formik?.values?.endDate));
                  }
                }}
                className={classes.radioGroup}
              >
                <FormControlLabel value="true" control={<Radio style={BlueRadio.root} checked={formik?.values?.isAbsenceAllStartDate === "true"} />} label="Oui" />
                <FormControlLabel value="false" control={<Radio style={BlueRadio.root} checked={formik?.values?.isAbsenceAllStartDate === "false"} />} label="Non" />
              </RadioGroup>
            </div>
          )}


          <div className={css({
            // marginBottom: 16
          })}>
            <FormikDatePicker
              formik={formik}
              fullWidth
              borderRadius={6}
              label="Date de fin"
              name="endDate"
              variant="outlined"
            />
          </div>

          {isSameDay(formik?.values?.startDate, formik?.values?.endDate) && (
            <div className={classes.radioContainer}>
              <div>Absent(e) toute la journée ?</div>
              <RadioGroup
                aria-labelledby="absenceAllStartDate"
                value={formik?.values?.isAbsenceAllStartDate}
                name="absenceAllStartDate"
                onChange={(e) => {
                  formik.setFieldValue("isAbsenceAllStartDate", e.target.value);
                  if (e.target.value === "true") {
                    formik.setFieldValue("hoursFirstDay", getPeriodHours(formik?.values?.startDate, formik?.values?.endDate));
                  }
                }}
                className={classes.radioGroup}
              >
                <FormControlLabel value="true" control={<Radio style={BlueRadio.root} checked={formik?.values?.isAbsenceAllStartDate === "true"} />} label="Oui" />
                <FormControlLabel value="false" control={<Radio style={BlueRadio.root} checked={formik?.values?.isAbsenceAllStartDate === "false"} />} label="Non" />
              </RadioGroup>
            </div>
          )}

          {isSameDay(formik?.values?.startDate, formik?.values?.endDate) && formik?.values?.isAbsenceAllStartDate === "false" && (
            <InputDuoStartDate
              formik={formik}
              hoursPerDay={getPeriodHours(formik?.values?.startDate, formik?.values?.endDate)}
              handleChangeHour={handleChangeHour}
              handleChangeMinute={handleChangeMinute}
              classes={classes}
            />
          )}

          {!(isSameDay(formik?.values?.startDate, formik?.values?.endDate)) && formik?.values?.isAbsenceAllEndDate === "false" && (
            <>
              <div className={classes.hourInput}>
                <FormikTextFieldPx
                  formik={formik}
                  fullWidth
                  type="number"
                  borderRadius={6}
                  label="Heures d'absence"
                  onChange={(e) => handleChangeHour(e, 0, getPeriodHours(formik?.values?.startDate, formik?.values?.endDate))}
                  name="hoursLastDay"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: getPeriodHours(formik?.values?.startDate, formik?.values?.endDate),
                    className: cx(global.infoText),
                  }}
                  variant="outlined"
                />
              </div>

              <div className={classes.hourInput}>
                <FormikTextFieldPx
                  formik={formik}
                  fullWidth
                  type="number"
                  borderRadius={6}
                  label="Minutes d'absence"
                  onChange={(e) => handleChangeMinute(e)}
                  name="minutesLastDay"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 59,
                  }}
                  variant="outlined"
                />
              </div>
            </>
          )}

          {!(isSameDay(formik?.values?.startDate, formik?.values?.endDate)) && (
            <div className={classes.radioContainer}>
              <div>Absent(e) toute la journée  ?</div>
              <RadioGroup
                aria-labelledby="absenceAllEndDate"
                value={formik?.values?.isAbsenceAllEndDate}
                name="absenceAllEndDate"
                onChange={(e) => {
                  formik.setFieldValue("isAbsenceAllEndDate", e.target.value);
                  if (e.target.value === "true") {
                    formik.setFieldValue("hoursLastDay", null);
                  }
                }}
                className={classes.radioGroup}
              >
                <FormControlLabel value="true" control={<Radio style={BlueRadio.root} checked={formik?.values?.isAbsenceAllEndDate === "true"} />} label="Oui" />
                <FormControlLabel value="false" control={<Radio style={BlueRadio.root} checked={formik?.values?.isAbsenceAllEndDate === "false"} />} label="Non" />
              </RadioGroup>
            </div>
          )}



          <div className={classes.hourInput}
            style={{
              // marginBottom: !student?.team ? "16px" : "0px"
            }}
          >
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              borderRadius={6}
              label="Motif"
              name="reason"
              inputProps={{
                className: cx(global.infoText),
              }}
              variant="outlined"
            />
          </div>

          {!student?.team?.business?.email && <div>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              borderRadius={6}
              label="Email de l'entreprise"
              name="business_email"
              inputProps={{
                className: cx(global.infoText),
              }}
              variant="outlined"
            />
          </div>}

          {formik.values.startDate.getTime() > formik.values.endDate.getTime() && <div>
            <InfoError text="La date de début est supérieure à la date de fin" />
          </div>}

          <Button
            variant="contained"
            color="primary"
            onClick={() => formik.handleSubmit()}
            className={classes.confirmButton}
          >
            {emailSendedLoading ? <CircularProgress size={24} color="white" /> : "Confirmer"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AbsenceDeclarationModal;
